import {
  ReactiveController,
  ReactiveControllerHost,
  ReactiveElement,
} from 'lit';

import { PersonalisationType, personalisation } from '@/context';

/**
 * The `PersonalisationEventController` class implements the `ReactiveController` interface
 * to manage the personalisation context of a host element. It listens for custom events
 * to update the personalisation context and triggers updates in the host element.
 *
 * The class constructor initializes the host and context, and adds the controller to the host.
 * The `_updateEventCallBack` method updates the context with new personalisation data and
 * requests an update in the host element.
 *
 * The `hostConnected` method adds an event listener for the `personalisation-context-update`
 * event when the host is connected. The `hostDisconnected` method removes the event listener
 * when the host is disconnected.
 */
export class PersonalisationEventController implements ReactiveController {
  private host: ReactiveControllerHost;

  public context: PersonalisationType[] = [];

  constructor(host: ReactiveControllerHost, context: any) {
    (this.host = host).addController(this);
    this.context = context;
  }

  private _updateEventCallBack({
    detail: { personalisationData, printOptionCode },
  }: CustomEvent) {
    this.context =
      personalisation.updateProviderListener(
        this.context,
        personalisationData,
        printOptionCode,
      ) || [];

    /**
     * Needed to trigger the willUpdate mechanism in web component logic
     */
    this.host.requestUpdate();
  }

  hostConnected() {
    const element = this.host as ReactiveElement;
    element.addEventListener('personalisation-context-update', event =>
      this._updateEventCallBack(event),
    );
  }

  hostDisconnected(): void {
    const element = this.host as ReactiveElement;
    element.removeEventListener('personalisation-context-update', event =>
      this._updateEventCallBack(event),
    );
  }
}
